<script setup lang="ts">
import type inputFullPhone from '@/components/base/form/InputFullPhone.vue';
import type { BankAccount } from '@/types/UserData';

type Props = {
  showSaveButton?: boolean;
  opacity?: boolean;
};

const { showSaveButton = true, opacity = false } = defineProps<Props>();
const emit = defineEmits(['check-pendency']);

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { ApiService } = useApiService();
const { openToaster } = useLayoutStore();
const { pixKey } = storeToRefs(useUserDataStore());
const { getUserPixKey } = useUserDataStore();
const { isMobile } = useLayout();
const { openDepositModal } = usePayments();

const {
  userEmail,
  userPhone,
  userPhoneDdi,
  userDocument,
  userHasDocument,
  userBankAccounts,
  fetchUserProfile,
} = useUserData();

const isEditing = ref<boolean>(false);
const isCreating = ref<boolean>(false);
const accountBankIdToBeDeleted = ref<number>();

function toggleEdit() {
  isEditing.value = !isEditing.value;
}

function onCancelButtonClick() {
  isEditing.value = false;
  isCreating.value = false;
}

const onEditButtonClick = () => {
  toggleEdit();
};

const isLoading = ref<boolean>(false);
const keyType = computed<string>(() => pixKey.value.type);
const keyValue = computed<string>(() => pixKey.value.value);

const pixType = ref<BankAccount['type_pix']>('CPF');
const pixValue = ref<string>('');

const initialValueKeyValue = computed(() => {
  if (pixType.value === 'PHONE' && !userPhone.value) {
    return keyValue.value ? keyValue.value : '';
  }

  return keyType.value === 'RANDOM' ? keyValue.value : '';
});

const inputPhoneRef = ref<InstanceType<typeof inputFullPhone> | null>(null);

const selectedPhoneNumber = computed(() => {
  return inputPhoneRef.value?.selectedPhoneNumber ?? '';
});

const selectedPhoneDdi = computed(() => {
  return inputPhoneRef.value?.selectedPhoneDdi ?? '';
});

const showInputPhone = computed<boolean>(() => {
  return pixType.value === 'PHONE' && (!userPhone.value || !userPhoneDdi.value);
});

const showInputKeyValue = computed<boolean>(() => {
  return pixType.value === 'RANDOM';
});

const maskType = computed<string | string[]>(() => {
  if (pixType.value === 'PHONE') {
    return ['(##) ####-####', '(##) #####-####'];
  }

  if (pixType.value === 'CPF') {
    return '###.###.###-##';
  }

  return '';
});

async function onBankAccountAddFormSubmit() {
  if (pixKeyError.value) {
    openToaster({
      message: pixKeyError.value,
      type: 'error',
    });

    return;
  }

  if (!isValid) {
    return;
  }

  await $containment.handle('update_data', () => {
    handleBankAccountAdd();
  });
}

async function handleBankAccountAdd() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  let finalKey;

  if (pixType.value === 'CPF') {
    finalKey = removeMask(pixValue.value);
  } else {
    finalKey = pixValue.value;
  }

  const payload: Partial<BankAccount> = {
    cpf: userDocument.value,
    key_pix: finalKey,
    type_pix: pixType.value,
  };

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  try {
    await ApiService.post('/bff/users/bank-account', payload);

    await fetchUserProfile({ force: true });

    isEditing.value = false;
    isCreating.value = false;
  } catch (err) {
    console.error('bank-account: creating', err);

    openToaster({
      message: t('account.user_data.errors.key_error'),
      type: 'error',
    });
  } finally {
    isLoading.value = false;
    emit('check-pendency');
    temporaryPassword.clear();
  }
}

function removeMask(str: string, number?: boolean) {
  if (number) {
    return str.replace(/[^\d]/gi, '');
  }

  return str.replace(/[^\w]/gi, '');
}

onMounted(async () => {
  await fetchUserProfile();

  if (!pixKey.value.type) {
    getUserPixKey();
  }
});

watch(
  () => pixType.value,
  (typeValue) => {
    if (typeValue === 'CPF') {
      pixValue.value = userDocument.value;
    }
    if (typeValue === 'EMAIL') {
      pixValue.value = userEmail.value;
    }
    if (typeValue === 'PHONE') {
      if (userPhoneDdi.value && userPhone.value) {
        pixValue.value = userPhoneDdi.value + userPhone.value;
      }
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => [userPhone.value, userPhoneDdi.value],
  ([phone, ddi]) => {
    if (pixType.value === 'PHONE') {
      if (!ddi || !phone) return;
      pixValue.value = ddi + phone;
    }
  },
);

watch(
  () => [selectedPhoneNumber.value, selectedPhoneDdi.value],
  ([phone, ddi]) => {
    if (
      pixType.value === 'PHONE' &&
      (!userPhone.value || !userPhoneDdi.value)
    ) {
      if (!ddi || !phone) return;
      pixValue.value = ddi + phone;
    }
  },
);

const pixKeyError = computed(() => {
  if (pixType.value === 'PHONE') {
    if (!userPhone.value || !userPhoneDdi.value) {
      if (!selectedPhoneNumber.value) {
        return t('account.user_data.errors.key_phone_error');
      }
      if (!selectedPhoneDdi.value) {
        return t('account.user_data.errors.key_ddi_error');
      }
    }
  }

  if (pixType.value === 'RANDOM') {
    const regexChaveAleatoria = /^[a-zA-Z0-9]{32}$/;
    const chaveSemMascara = pixValue.value.replace(/\W/g, '');

    if (
      !regexChaveAleatoria.test(chaveSemMascara) ||
      chaveSemMascara.length < 32
    ) {
      return t('account.user_data.errors.key_random_error');
    }
  }

  if (!pixValue.value || pixValue.value === '') {
    return t('account.user_data.errors.key_value_error');
  }

  return '';
});

const selectedKeyType = computed(() => {
  return pixType.value;
});

const selectedKeyValue = computed(() => {
  return pixValue.value;
});

function cpfMask(cpf: string) {
  if (!cpf || cpf === '') return '';

  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/gi, '$1.$2.$3-$4');
}

function onAddBankAccountButtonClick() {
  isEditing.value = true;
  isCreating.value = true;
}

async function onRemoveBankAccountButtonClick() {
  if (!accountBankIdToBeDeleted.value) {
    return;
  }

  await $containment.handle('update_data', () => {
    handleRemoveBankAccount();
  });
}

async function handleRemoveBankAccount() {
  isLoading.value = true;

  try {
    const id = accountBankIdToBeDeleted.value;

    await ApiService.delete(`/bff/users/bank-account/${id}`);

    await fetchUserProfile({ force: true });

    isEditing.value = false;
  } catch (err) {
    console.error('bank-account: removing', err);

    openToaster({
      message: t('account.user_data.errors.key_error'),
      type: 'error',
    });
  } finally {
    isLoading.value = false;
    emit('check-pendency');
  }
}

const isValid = computed<boolean>(() => {
  if (pixKeyError.value) {
    return false;
  }

  return !!userDocument.value;
});

const hasPendency = ref<boolean>(false);

defineExpose({
  name: 'bank-accounts',
  selectedKeyType,
  selectedKeyValue,
  selectedPhoneNumber,
  selectedPhoneDdi,
  pixKeyError,
  hasPendency,
  toggleEdit,
  isEditing,
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div :class="[$style.toggleBox, { [$style.opacity]: opacity }]">
      <div :class="$style.header">
        <div :class="$style.title">
          {{ $t('account.user_data.bank_accounts') }}
        </div>
        <button
          v-if="isEditing || isCreating"
          :class="$style.button"
          @click="onCancelButtonClick"
        >
          {{ $t('general.cancel') }}
        </button>
        <button
          v-else
          :class="$style.button"
          @click="onEditButtonClick"
        >
          {{ $t('general.edit') }}
        </button>
      </div>

      <div
        v-if="!isEditing"
        :class="$style.viewContent"
      >
        <UserAccountBankAccountsList />

        <template v-if="pixKey.type && !userBankAccounts?.length">
          <UserAccountBankAccountsListItem
            :bank-account="{ key_pix: pixValue, type_pix: pixType }"
          />

          <p :class="$style.additionalInfo">
            <span>{{ $t('account.user_data.account_additional_info') }}</span>
          </p>
        </template>

        <button
          :class="$style.addButton"
          @click.prevent="onAddBankAccountButtonClick"
        >
          {{ $t('account.user_data.add_account') }}
        </button>
      </div>

      <div
        v-else
        :class="$style.editContent"
      >
        <template v-if="userHasDocument">
          <form
            v-if="isCreating || !userBankAccounts?.length"
            :class="$style.componentBox"
            @submit.prevent="onBankAccountAddFormSubmit"
          >
            <div :class="$style.componentBox">
              <div
                class="max-w-full md:max-w-60"
                :class="$style.formGroup"
              >
                <label for="fieldPixType">
                  {{ $t('account.user_data.key_type') }}
                </label>

                <BaseFormSelectField
                  id="fieldPixType"
                  v-model="pixType"
                  :initial-value="keyType"
                  name="fieldPixType"
                  :options="[
                    { value: 'CPF', label: 'CPF' },
                    { value: 'PHONE', label: 'Telefone' },
                    { value: 'EMAIL', label: 'E-mail' },
                    { value: 'RANDOM', label: 'Chave Aleatória' },
                  ]"
                />
              </div>

              <div
                class="max-w-full"
                :class="$style.formGroup"
              >
                <label for="fieldPixValue">
                  {{ $t('account.user_data.key') }}
                </label>

                <template v-if="showInputPhone">
                  <BaseFormInputFullPhone ref="inputPhoneRef" />
                </template>

                <template v-else-if="!showInputKeyValue">
                  <div :class="$style.inputInfo">
                    {{ isMobile ? $t('account.user_data.key') : pixType }}:
                    <strong class="text-primary">
                      {{ pixValue }}
                    </strong>
                  </div>
                </template>

                <template v-else>
                  <BaseFormInput
                    id="fieldPixValue"
                    v-model="pixValue"
                    autocomplete="off"
                    :data-maska="maskType"
                    :initial-value="initialValueKeyValue"
                    name="fieldPixValue"
                    :placeholder="$t('account.user_data.key')"
                    :readonly="!showInputKeyValue"
                    type="text"
                  />
                </template>
              </div>
            </div>

            <div
              :class="$style.thirdAlert"
              v-html="
                $t('account.user_data.pix_info', { cpf: cpfMask(userDocument) })
              "
            />

            <div
              v-if="showSaveButton"
              class="flex justify-between items-start w-full min-w-[50%]"
            >
              <button
                :class="$style.btnSubmit"
                :disabled="!isValid"
                type="submit"
              >
                <NuxtIcon name="check" />
                {{ $t('account.save') }}
              </button>
            </div>
          </form>

          <div
            v-else
            :class="$style.viewContent"
          >
            <UserAccountBankAccountsList
              v-model="accountBankIdToBeDeleted"
              pickable
            />

            <button
              :class="$style.removeButton"
              :disabled="!accountBankIdToBeDeleted"
              @click.prevent="onRemoveBankAccountButtonClick"
            >
              {{ $t('account.user_data.remove_account') }}
            </button>
          </div>
        </template>

        <template v-else>
          <div
            :class="$style.makeYourDeposit"
            @click="openDepositModal()"
          >
            <span class="cursor-pointer">
              {{ $t('account.user_data.make_your_deposit') }}
            </span>
            <button :class="$style.btnDeposit">
              {{ $t('header.deposit') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.toggleBox {
  @apply flex flex-col gap-3 p-1 md:p-0 px-1 md:px-2;

  &.opacity {
    @apply opacity-20 pointer-events-none select-none;
  }

  .header {
    @apply flex items-center justify-between;

    .title {
      @apply text-sm font-semibold;
    }

    .pill {
      @apply bg-texts/20 text-white px-1.5 py-0.5 rounded-full text-[.6rem] font-semibold leading-3 mr-auto;

      &.redPill {
        @apply bg-error;
      }

      &.greenPill {
        @apply bg-success;
      }
    }

    .button {
      @apply text-2xs md:text-xs font-bold uppercase opacity-60 px-1;
    }
  }

  .viewContent {
    @apply text-xs md:text-sm text-texts/80;

    .additionalInfo {
      @apply text-sm md:text-sm text-warning/70 max-w-[540px];
    }

    .addButton {
      @apply text-xs text-primary mt-3 text-left;
    }

    .removeButton {
      @apply bg-error text-texts py-2 px-4 mt-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;

      &[disabled]:not([disabled='false']) {
        @apply opacity-40 cursor-not-allowed;
      }
    }
  }

  .editContent {
    @apply w-full;
  }
}

.componentBox {
  @apply w-full flex items-start justify-start flex-wrap gap-4;

  .formGroup {
    @apply flex-1 w-full relative min-w-[50%] md:min-w-[20%];

    label {
      @apply text-xs font-medium;
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;

    &[disabled] {
      @apply opacity-40 cursor-not-allowed;
    }
  }

  .inputInfo {
    @apply text-xs bg-primary/10 text-primary rounded px-3 py-2 h-auto md:h-[50px] flex items-center justify-start gap-2;
  }

  .thirdAlert {
    @apply text-warning text-xs mb-2 px-0.5 w-full;

    strong {
      @apply font-bold whitespace-nowrap;
    }
  }
}

.makeYourDeposit {
  @apply flex flex-col md:flex-row justify-center md:justify-between items-center gap-2 border border-dotted border-texts/40 text-texts/50 rounded-lg py-5 px-6 cursor-pointer text-center md:text-left;

  .btnDeposit {
    @apply bg-button-bg text-button-text font-semibold px-4 py-2 rounded-md text-sm cursor-pointer;
  }
}
</style>
